import { forwardRef, useCallback, useEffect, useState } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import {
  IMAGE_BORDER_STYLES,
  useImageBorder,
} from "@/tiptap/ui/image-border-button"

// --- UI Primitive ---
import { Button } from "@/tiptap/ui-primitive/button"
import { Input } from "@/tiptap/ui-primitive/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/tiptap/ui-primitive/popover"
import {
  Card,
  CardBody,
  CardGroupLabel,
  CardItemGroup,
} from "@/tiptap/ui-primitive/card"

function ImageBorderContent({
  values,
  onSubmit,
}) {
  const [borderWidth, setBorderWidth] = useState(values.borderWidth || "")
  const [borderStyle, setBorderStyle] = useState(values.borderStyle || "")

  useEffect(() => {
    setBorderWidth(values.borderWidth || "")
    setBorderStyle(values.borderStyle || "")
  }, [values.borderStyle, values.borderWidth])

  const handleSubmit = useCallback((event) => {
    event.preventDefault()
    onSubmit({
      borderWidth,
      borderStyle,
    })
  }, [borderStyle, borderWidth, onSubmit])

  const handleBorderWidthChange = useCallback((event) => {
    setBorderWidth(event.target.value.replace(/\D/g, ""))
  }, [])

  const stopEditorHotkeys = useCallback((event) => {
    event.stopPropagation()
  }, [])

  return (
    <Card style={{ width: "16rem" }} onKeyDownCapture={stopEditorHotkeys}>
      <CardBody>
        <form onSubmit={handleSubmit}>
          <CardItemGroup>
            <div>
              <CardGroupLabel>Border width</CardGroupLabel>
              <Input
                type="text"
                inputMode="numeric"
                value={borderWidth}
                placeholder="0"
                pattern="[0-9]*"
                onChange={handleBorderWidthChange}
                autoFocus />
            </div>

            <div>
              <CardGroupLabel>Border style</CardGroupLabel>
              <select
                className="tiptap-input"
                value={borderStyle}
                aria-label="Border style"
                onChange={(event) => setBorderStyle(event.target.value)}>
                {IMAGE_BORDER_STYLES.map((style) => (
                  <option key={style.value || "blank"} value={style.value}>
                    {style.label}
                  </option>
                ))}
              </select>
            </div>

            <Button
              type="submit"
              variant="ghost"
              className="tiptap-media-form-submit"
              style={{ marginTop: "5px" }}>
              <span className="tiptap-button-text">Apply</span>
            </Button>
          </CardItemGroup>
        </form>
      </CardBody>
    </Card>
  );
}

export const ImageBorderButton = forwardRef((
  {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onSet,
    onOpenChange,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const [isOpen, setIsOpen] = useState(false)
  const {
    isVisible,
    canSet,
    values,
    handleSetBorder,
    label,
    Icon,
  } = useImageBorder({
    editor,
    hideWhenUnavailable,
    onSet,
  })

  const handleOpenChange = useCallback((nextIsOpen) => {
    setIsOpen(nextIsOpen)
    onOpenChange?.(nextIsOpen)
  }, [onOpenChange])

  const handleClick = useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    setIsOpen(!isOpen)
  }, [isOpen, onClick])

  const handleSubmit = useCallback((nextValues) => {
    if (handleSetBorder(nextValues)) {
      setIsOpen(false)
    }
  }, [handleSetBorder])

  if (!isVisible) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          data-active-state={values.borderStyle || values.borderWidth ? "on" : "off"}
          role="button"
          tabIndex={-1}
          disabled={!canSet}
          data-disabled={!canSet}
          aria-label={label}
          tooltip={label}
          onClick={handleClick}
          {...buttonProps}
          ref={ref}>
          {children ?? <Icon className="tiptap-button-icon" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="center">
        <ImageBorderContent values={values} onSubmit={handleSubmit} />
      </PopoverContent>
    </Popover>
  );
})

ImageBorderButton.displayName = "ImageBorderButton"
